import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  StepConnector,
  TextField,
  Typography,
  LinearProgress,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  ListItemAvatar,
  Link,
  stepConnectorClasses,
  styled,
  useTheme,
  alpha,
  Snackbar,
  FormControl,
  FormHelperText,
  Badge
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  Comment as CommentIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Timeline as TimelineIcon,
  Send as SendIcon,
  Done as DoneIcon,
  MoreHoriz as MoreHorizIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Notifications as NotificationsIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  EventNote as EventNoteIcon,
  Person as PersonIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  AttachFile as AttachFileIcon,
  Cached as CachedIcon,
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
  FileOpen as FileOpenIcon,
  CloudUpload as CloudUploadIcon,
  DeleteOutline as DeleteOutlineIcon
} from '@mui/icons-material';
import workflowService from '../../../services/workflowService';

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300],
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const QontoStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
  display: 'flex',
  height: 22,
  alignItems: 'center',
  ...(ownerState.active && {
    color: theme.palette.primary.main,
  }),
  '& .QontoStepIcon-completedIcon': {
    color: theme.palette.primary.main,
    zIndex: 1,
    fontSize: 18,
  },
  '& .QontoStepIcon-circle': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
  },
}));

function QontoStepIcon(props) {
  const { active, completed, className } = props;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <CheckIcon className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

const getStatusColor = (status) => {
  if (status.toLowerCase().includes('reject') || status.toLowerCase().includes('decline') || 
      status.toLowerCase().includes('cancel')) {
    return 'error';
  } else if (status.toLowerCase().includes('approve') || status.toLowerCase().includes('complete')) {
    return 'success';
  } else if (status.toLowerCase().includes('review') || status.toLowerCase().includes('progress')) {
    return 'warning';
  } else if (status.toLowerCase().includes('wait') || status.toLowerCase().includes('pending')) {
    return 'info';
  }
  return 'default';
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getFileIcon = (fileName) => {
  if (!fileName) return <FileIcon />;
  
  const extension = fileName.split('.').pop().toLowerCase();
  
  if (['pdf'].includes(extension)) {
    return <PdfIcon color="error" />;
  } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
    return <ImageIcon color="primary" />;
  } else {
    return <FileIcon color="action" />;
  }
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const MemberWorkflowDetailPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [actionComment, setActionComment] = useState('');
  const [actionFormData, setActionFormData] = useState({});
  
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [processing, setProcessing] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState(null);
  const [optimisticStep, setOptimisticStep] = useState(null);

  const [previewFile, setPreviewFile] = useState(null);
  
  // File upload related state
  const [attachments, setAttachments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  // File upload related handlers
  const handleFileAttachment = (event) => {
    const files = Array.from(event.target.files);
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB limit
    const validFiles = files.filter((file) => file.size <= maxSizeInBytes);
    
    if (validFiles.length < files.length) {
      setError('Some files were not added because they exceed the 10MB size limit.');
    }
    
    setAttachments((prev) => [...prev, ...validFiles]);
    event.target.value = '';
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDownloadFile = (file) => {
    if (file.path) {
      // If the file is already uploaded and has a path from the server
      const fileUrl = `${import.meta.env.VITE_API_URL}${file.path}`;
      
      // Open in new tab
      window.open(fileUrl, '_blank');
    } else {
      // For local files not yet uploaded, create a blob URL
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);
    }
  };

  const handleUploadAttachments = async () => {
    if (attachments.length === 0) return;
    
    setIsUploading(true);
    setError('');
    
    try {
      const uploadFormData = new FormData();
      attachments.forEach((file, index) => {
        uploadFormData.append(`attachment${index}`, file);
      });
      
      await workflowService.addAttachmentsToWorkflow(requestId, uploadFormData, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percent: percentCompleted,
        });
      });
      
      setAttachments([]);
      setUploadProgress({});
      setSuccessMessage('Files uploaded successfully');
      setSuccess(true);
      
      // Refresh workflow data to get the updated attachments
      refreshWorkflowData();
    } catch (err) {
      console.error('Error uploading attachments:', err);
      setError(err.message || 'An error occurred while uploading files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        setLoading(true);
        const response = await workflowService.getRequestById(requestId);
        setRequest(response.data);
        
        if (response.data.workflowTemplate && response.data.workflowTemplate.steps) {
          const currentStepIndex = response.data.workflowTemplate.steps.findIndex(
            step => step._id === response.data.currentStep
          );
          
          if (currentStepIndex !== -1) {
            setActiveStep(currentStepIndex);
          }
        }
      } catch (err) {
        console.error('Error fetching workflow request details:', err);
        setError(err.message || 'Error loading the request details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchRequest();
    }
  }, [requestId]);

  useEffect(() => {
    if (request && request.workflowTemplate && request.workflowTemplate.steps && request.currentStep) {
      const currentStepIndex = request.workflowTemplate.steps.findIndex(
        step => step._id === request.currentStep
      );
      
      if (currentStepIndex !== -1 && currentStepIndex !== activeStep) {
        setActiveStep(currentStepIndex);
      }
    }
  }, [request, activeStep]);

  const refreshWorkflowData = async () => {
    try {
      setLoading(true);
      const response = await workflowService.getRequestById(requestId);
      
      setRequest(response.data);
      
      if (response.data?.workflowTemplate?.steps && response.data?.currentStep) {
        const currentStepIndex = response.data.workflowTemplate.steps.findIndex(
          step => step._id === response.data.currentStep
        );
        
        if (currentStepIndex !== -1) {
          setActiveStep(currentStepIndex);
        }
      }
      
      setSuccessMessage('Workflow data refreshed');
      setSuccess(true);
    } catch (err) {
      console.error('Error refreshing workflow data:', err);
      setError('Failed to refresh workflow data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    
    setIsSubmitting(true);
    try {
      const response = await workflowService.addComment(requestId, comment);
      setRequest(response.data);
      setComment('');
      setSuccessMessage('Comment added successfully');
      setSuccess(true);
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Failed to add comment: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActionClick = (action) => {
    setCurrentAction(action);
    setActionComment('');
    setActionFormData({});
    setOpenActionDialog(true);
  };

  const handleAction = async () => {
    if (!currentAction) return;
    
    setIsSubmitting(true);
    setProcessing(true);
    
    if (currentAction.nextStepId && request.workflowTemplate && request.workflowTemplate.steps) {
      const nextStepIndex = request.workflowTemplate.steps.findIndex(
        step => step._id === currentAction.nextStepId || 
               String(step.order) === String(currentAction.nextStepId)
      );
      
      if (nextStepIndex !== -1) {
        setOptimisticStep(nextStepIndex);
        setActiveStep(nextStepIndex);
      }
    }
    
    if (currentAction.resultStatus) {
      setOptimisticStatus(currentAction.resultStatus);
    }
    
    try {
      const response = await workflowService.performAction(
        requestId,
        currentAction.id,
        actionComment
      );
      
      if (response && response.data) {
        const updatedRequest = response.data;
        
        if (updatedRequest.workflowTemplate && updatedRequest.workflowTemplate.steps && updatedRequest.currentStep) {
          const newStepIndex = updatedRequest.workflowTemplate.steps.findIndex(
            step => step._id === updatedRequest.currentStep
          );
          
          if (newStepIndex !== -1) {
            setActiveStep(newStepIndex);
            setOptimisticStep(null);
          }
        }
        
        setRequest(updatedRequest);
        setOptimisticStatus(null);
      }
      
      setSuccess(true);
      setSuccessMessage(`Action '${currentAction.label}' performed successfully!`);
      
      setOpenActionDialog(false);
      setActionComment('');
      setCurrentAction(null);
      
      setTimeout(() => {
        setSuccess(false);
        setSuccessMessage('');
      }, 5000);
    } catch (err) {
      console.error('Error performing action:', err);
      let errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      
      setOptimisticStatus(null);
      setOptimisticStep(null);
      
      if (request?.workflowTemplate?.steps && request?.currentStep) {
        const currentStepIndex = request.workflowTemplate.steps.findIndex(
          step => step._id === request.currentStep
        );
        if (currentStepIndex !== -1) {
          setActiveStep(currentStepIndex);
        }
      }
      
      if (err.response?.data?.availableActions) {
        const actions = err.response.data.availableActions.all ?
          err.response.data.availableActions.all.map(a => `${a.id} (${a.label})`).join(', ') :
          'No available actions';
        errorMessage += `. Available actions: ${actions}`;
      }
      
      setError(`Failed to perform action: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
      setProcessing(false);
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    
    return status
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^\w/, c => c.toUpperCase());
  };

  const getAvailableActions = () => {
    if (!request || !request.currentStep || !Array.isArray(request.currentStep.actions)) {
      return [];
    }
    
    return request.currentStep.actions.filter(action => 
      action.performer === 'member' || action.performer === 'both'
    );
  };

  const renderActionForm = () => {
    if (!currentAction || !currentAction.formFields || currentAction.formFields.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Please provide the following information:
        </Typography>
        
        {currentAction.formFields.map((field, index) => {
          switch (field.type) {
            case 'text':
              return (
                <TextField
                  key={index}
                  label={field.label}
                  fullWidth
                  required={field.required}
                  value={actionFormData[field.name] || ''}
                  onChange={(e) => setActionFormData({ ...actionFormData, [field.name]: e.target.value })}
                  margin="normal"
                  variant="outlined"
                  helperText={field.helpText}
                />
              );
            case 'textarea':
              return (
                <TextField
                  key={index}
                  label={field.label}
                  fullWidth
                  required={field.required}
                  value={actionFormData[field.name] || ''}
                  onChange={(e) => setActionFormData({ ...actionFormData, [field.name]: e.target.value })}
                  margin="normal"
                  variant="outlined"
                  multiline
                  rows={4}
                  helperText={field.helpText}
                />
              );
            default:
              return (
                <TextField
                  key={index}
                  label={field.label}
                  fullWidth
                  required={field.required}
                  value={actionFormData[field.name] || ''}
                  onChange={(e) => setActionFormData({ ...actionFormData, [field.name]: e.target.value })}
                  margin="normal"
                  variant="outlined"
                  helperText={field.helpText}
                />
              );
          }
        })}
      </Box>
    );
  };

  const getStatusIcon = (status) => {
    if (status.toLowerCase().includes('reject') || status.toLowerCase().includes('decline') || 
        status.toLowerCase().includes('cancel')) {
      return <CancelIcon />;
    } else if (status.toLowerCase().includes('approve') || status.toLowerCase().includes('complete')) {
      return <CheckCircleIcon />;
    } else if (status.toLowerCase().includes('review') || status.toLowerCase().includes('progress')) {
      return <MoreHorizIcon />;
    } else if (status.toLowerCase().includes('wait') || status.toLowerCase().includes('pending')) {
      return <HourglassEmptyIcon />;
    }
    return <InfoIcon />;
  };

  const handleFilePreview = (attachment) => {
    const fileUrl = `${import.meta.env.VITE_API_URL}${attachment.path}`;
    setPreviewFile({
      name: attachment.originalName,
      url: fileUrl,
      type: attachment.mimetype
    });
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/member/workflows')}>
          Back to Workflows
        </Button>
      </Box>
    );
  }

  if (!request) {
    return (
      <Box sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Request not found
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/member/workflows')}>
          Back to Workflows
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          {request.workflowTemplate?.name || 'Workflow Request'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            color="primary"
            onClick={refreshWorkflowData}
            disabled={loading}
            startIcon={<CachedIcon />}
          >
            Refresh
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/member/workflows')}
            startIcon={<ArrowBackIcon />}
          >
            Back to Workflows
          </Button>
        </Box>
      </Box>

      {processing && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress
            color="primary"
            sx={{
              height: 6,
              borderRadius: 3
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            Processing action...
          </Typography>
        </Box>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        {request.workflowTemplate && request.workflowTemplate.steps && request.workflowTemplate.steps.length > 0 ? (
          <Stepper 
            activeStep={optimisticStep !== null ? optimisticStep : activeStep} 
            alternativeLabel 
            connector={<QontoConnector />}
            sx={{ mb: 4 }}
          >
            {request.workflowTemplate.steps.map((step, index) => {
              const stepProps = {};
              if (index < (optimisticStep !== null ? optimisticStep : activeStep)) {
                stepProps.completed = true;
              }
              
              return (
                <Step key={step._id || index} {...stepProps}>
                  <StepLabel 
                    StepIconComponent={QontoStepIcon}
                    sx={{
                      '& .MuiStepLabel-label': {
                        mt: 1,
                        fontWeight: index === (optimisticStep !== null ? optimisticStep : activeStep) ? 600 : 400,
                        color: index === (optimisticStep !== null ? optimisticStep : activeStep) ? 'primary.main' : 'text.secondary',
                      }
                    }}
                  >
                    {step.name}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        ) : (
          <Box sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body1" color="text.secondary" align="center">
              This workflow process does not have predefined steps.
            </Typography>
          </Box>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardHeader title="Request Details" />
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack spacing={2}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: (() => {
                      const statusColor = getStatusColor(request.currentStatus);
                      return theme.palette[statusColor] && theme.palette[statusColor].main
                        ? alpha(theme.palette[statusColor].main, 0.08)
                        : alpha(theme.palette.primary.main, 0.08);
                    })(),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: (() => {
                          const statusColor = getStatusColor(request.currentStatus);
                          return theme.palette[statusColor] && theme.palette[statusColor].main
                            ? theme.palette[statusColor].main
                            : theme.palette.primary.main;
                        })()
                      }}
                    >
                      {getStatusIcon(request.currentStatus)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Current Status
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatStatus(request.currentStatus)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Title
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ 
                        p: 2, 
                        mt: 1, 
                        borderRadius: 1,
                        borderColor: alpha(theme.palette.primary.main, 0.2)
                      }}
                    >
                      <Typography variant="body1">
                        {request.title}
                      </Typography>
                    </Paper>
                  </Box>

                  {request.description && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Description
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ 
                          p: 2, 
                          mt: 1, 
                          borderRadius: 1,
                          borderColor: alpha(theme.palette.primary.main, 0.2)
                        }}
                      >
                        <Typography variant="body1">
                          {request.description}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {request.formData && Object.keys(request.formData).length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Form Data
                      </Typography>
                      <List sx={{ 
                        mt: 1, 
                        border: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        borderRadius: 1
                      }}>
                        {Object.entries(request.formData).map(([key, value], index) => (
                          <ListItem 
                            key={key}
                            divider={index < Object.entries(request.formData).length - 1}
                          >
                            <ListItemText
                              primary={
                                <Typography variant="subtitle2" color="text.secondary">
                                  {key.replace(/([A-Z])/g, ' $1')
                                    .replace(/_/g, ' ')
                                    .replace(/^\w/, c => c.toUpperCase())}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="body1" sx={{ mt: 0.5 }}>
                                  {Array.isArray(value)
                                    ? value.join(', ')
                                    : (typeof value === 'object'
                                      ? JSON.stringify(value, null, 2)
                                      : String(value))}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Timeline
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Request Created
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(request.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EventNoteIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Last Updated
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(request.updatedAt)}
                          </Typography>
                        </Box>
                      </Box>

                      {request.currentStep && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Current Step
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {request.currentStep.name}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardHeader title="Actions Required" />
              <CardContent>
                {getAvailableActions().length > 0 ? (
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      The following actions are available at this step:
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      {getAvailableActions().map(action => (
                        <Button
                          key={action.id}
                          variant="contained"
                          color={(() => {
                            if (action.type === 'approve') return 'success';
                            if (action.type === 'reject') return 'error';
                            if (action.type === 'submit') return 'primary';
                            if (action.type === 'request_info') return 'info';
                            return 'primary';
                          })()}
                          onClick={() => handleActionClick(action)}
                          sx={{ mb: 1 }}
                        >
                          {action.label || action.type}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ 
                    p: 3, 
                    textAlign: 'center', 
                    bgcolor: 'background.default', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <NotificationsIcon color="action" sx={{ fontSize: 40, mb: 1, opacity: 0.6 }} />
                    <Typography variant="body1" paragraph>
                      No actions are required from you at this time.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your request is being processed. We'll notify you when there's an update or when an action is required.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Comments & Updates
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Add a comment"
            placeholder="Type your comment here..."
            multiline
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={isSubmitting}
          />
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={!comment.trim() || isSubmitting}
              onClick={handleAddComment}
              endIcon={<SendIcon />}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Add Comment'}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          {request.history && request.history.length > 0 ? (
            <Stack spacing={2}>
              {request.history.map((historyItem, index) => (
                <Card 
                  key={index} 
                  variant="outlined" 
                  sx={{
                    bgcolor: historyItem.actor === 'admin' ? 'grey.100' : 'primary.50'
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {historyItem.actor === 'admin' ? 'Administrator' : 'You'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(historyItem.timestamp)}
                      </Typography>
                    </Box>
                    <Typography variant="body2">{historyItem.description}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              No comments or history yet
            </Typography>
          )}
        </Box>
      </Paper>

      {request.attachments && request.attachments.length > 0 && (
        <Card sx={{ mt: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <CardHeader 
            title="Attachments" 
            sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
            avatar={<AttachFileIcon color="primary" />}
          />
          <CardContent>
            <List>
              {request.attachments.map((attachment, index) => (
                <ListItem 
                  key={index} 
                  divider={index < request.attachments.length - 1}
                  sx={{ py: 1.5 }}
                >
                  <ListItemAvatar>
                    <Avatar variant="rounded" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      {getFileIcon(attachment.originalName)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={attachment.originalName}
                    secondary={formatFileSize(attachment.size)}
                  />
                  <Box sx={{ display: 'flex' }}>
                    <Tooltip title="Preview">
                      <Button
                        variant="outlined"
                        size="small"
                        color="info"
                        sx={{ mr: 1, minWidth: '36px', p: '4px' }}
                        onClick={() => handleFilePreview(attachment)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Download">
                      <Button
                        variant="outlined"
                        size="small"
                        component="a"
                        href={`${import.meta.env.VITE_API_URL}${attachment.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={attachment.originalName}
                        sx={{ minWidth: '36px', p: '4px' }}
                      >
                        <GetAppIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </Box>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* File Upload Section */}
      <Card sx={{ mt: 3, mb: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        <CardHeader 
          title="Upload Additional Files" 
          sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
          avatar={<CloudUploadIcon color="primary" />}
        />
        <CardContent>
          <Box>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                borderStyle: 'dashed',
                borderRadius: 2,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.04)
              }}
            >
              <input
                type="file"
                id="file-upload"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileAttachment}
              />
              <label htmlFor="file-upload">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 1 }}
                >
                  Upload Files
                </Button>
              </label>
              <Typography variant="caption" display="block" color="text.secondary">
                Drag and drop files here or click to browse. Maximum file size: 10MB
              </Typography>
            </Paper>
            
            {attachments.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {attachments.length} file(s) ready to upload
                </Typography>
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  <List disablePadding>
                    {attachments.map((file, index) => (
                      <ListItem
                        key={`${file.name}-${index}`}
                        divider={index < attachments.length - 1}
                        sx={{ px: 2, py: 1 }}
                      >
                        <ListItemAvatar>
                          <Avatar 
                            variant="rounded" 
                            sx={{ bgcolor: 'background.paper', cursor: 'pointer' }}
                            onClick={() => handleDownloadFile(file)}
                          >
                            {getFileIcon(file.name)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={file.name}
                          secondary={formatFileSize(file.size)}
                          primaryTypographyProps={{ 
                            variant: 'body2', 
                            noWrap: true,
                            sx: { cursor: 'pointer' } 
                          }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                          onClick={() => handleDownloadFile(file)}
                        />
                        <Button
                          edge="end"
                          onClick={() => handleRemoveAttachment(index)}
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                        >
                          Remove
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}
            
            {isUploading && Object.keys(uploadProgress).length > 0 && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Uploading files: {uploadProgress.percent}% complete
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress.percent}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                disabled={attachments.length === 0 || isUploading}
                onClick={handleUploadAttachments}
                startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              >
                {isUploading ? 'Uploading...' : 'Upload Files'}
              </Button>
            </Box>
            
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: 'info.lighter', 
              borderRadius: 2, 
              border: '1px solid', 
              borderColor: 'info.light'
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start' 
                }}
              >
                <InfoIcon color="info" fontSize="small" sx={{ mr: 1, mt: 0.25 }} />
                You can upload additional supporting documents at any time during the workflow process.
                These documents will be attached to your request and visible to all workflow participants.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={!!previewFile}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" noWrap sx={{ maxWidth: '80%' }}>
            {previewFile?.name}
          </Typography>
          <Box>
            <IconButton 
              edge="end" 
              aria-label="download" 
              href={previewFile?.url}
              download={previewFile?.name}
              component="a"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ mr: 1 }}
              title="Download"
            >
              <GetAppIcon />
            </IconButton>
            <IconButton 
              edge="end" 
              aria-label="open" 
              href={previewFile?.url}
              target="_blank"
              rel="noopener noreferrer"
              component="a"
              size="small"
              sx={{ mr: 1 }}
              title="Open in New Tab"
            >
              <FileOpenIcon />
            </IconButton>
            <IconButton 
              edge="end" 
              aria-label="close" 
              onClick={handleClosePreview}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {previewFile && (
            previewFile.type.startsWith('image/') ? (
              <img 
                src={previewFile.url} 
                alt={previewFile.name} 
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
              />
            ) : previewFile.type === 'application/pdf' ? (
              <iframe 
                src={previewFile.url} 
                width="100%" 
                height="100%" 
                title={previewFile.name}
                style={{ border: 'none' }}
              />
            ) : (
              <Box sx={{ 
                p: 4, 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Box sx={{ mb: 2 }}>
                  {getFileIcon(previewFile.name)}
                </Box>
                <Typography variant="body1" gutterBottom>
                  Preview not available for this file type
                </Typography>
                <Button 
                  variant="contained" 
                  href={previewFile.url}
                  download={previewFile.name}
                  component="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<GetAppIcon />}
                  sx={{ mt: 2 }}
                >
                  Download File
                </Button>
              </Box>
            )
          )}
        </DialogContent>
      </Dialog>

      <Dialog 
        open={openActionDialog} 
        onClose={() => !isSubmitting && setOpenActionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{currentAction?.label || 'Perform Action'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {currentAction?.description || 'Please provide any necessary details to proceed with this action.'}
          </DialogContentText>
          
          {renderActionForm()}
          
          <TextField
            margin="dense"
            label="Comments"
            fullWidth
            multiline
            rows={3}
            value={actionComment}
            onChange={(e) => setActionComment(e.target.value)}
            disabled={isSubmitting}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenActionDialog(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleAction}
            color={(() => {
              if (currentAction?.type === 'approve') return 'success';
              if (currentAction?.type === 'reject') return 'error';
              return 'primary';
            })()}
            variant="contained"
            loading={isSubmitting}
            loadingPosition="end"
            endIcon={<SendIcon />}
          >
            Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={success}
        autoHideDuration={5000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSuccess(false)} 
          severity="success"
          variant="filled"
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MemberWorkflowDetailPage;
